import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, CalendarCheck } from "lucide-react";
import { getPaymentStatus } from "../lib/api";
import { useCart } from "../context/CartContext";
import { BUSINESS_NUMBER } from "../lib/brand";
import SchedulePicker from "../components/SchedulePicker";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [info, setInfo] = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const { clearCart } = useCart();
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        setInfo(res);
        if (res.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (res.payment_status === "failed" || res.payment_status === "expired") {
          setState("failed");
          return;
        }
      } catch {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main className="max-w-xl mx-auto px-6 mt-20 min-h-[50vh] text-center">
      {state === "checking" && (
        <div className="flex flex-col items-center">
          <Loader2 size={44} className="text-[#0e2a1e] animate-spin" />
          <h1 className="font-serif-brand text-3xl font-semibold text-[#0e2a1e] mt-6">Confirming your payment…</h1>
          <p className="text-[#0e2a1e]/60 mt-2">Hang tight, this only takes a moment.</p>
        </div>
      )}
      {state === "paid" && (
        <div className="flex flex-col items-center fade-up">
          <CheckCircle2 size={52} className="text-[#2f6b1e]" />
          <h1 className="font-serif-brand text-3xl font-semibold text-[#0e2a1e] mt-6">
            {info?.kind === "booking" ? "Session reserved!" : info?.kind === "donation" ? "Thank you for your donation!" : "Thank you for your order!"}
          </h1>
          <p className="text-[#0e2a1e]/70 mt-3 leading-relaxed">
            {info?.kind === "booking"
              ? "Your deposit is received. Pick a preferred session time below."
              : info?.kind === "donation"
              ? "Your generosity means the world to us. Thank you for supporting Everyone's Budget and our Mississauga community."
              : "We've received your payment. Choose your pick-up time below."}
          </p>
          <p className="text-[#0e2a1e]/50 text-sm mt-4">A receipt has been emailed to you.</p>
          {info?.kind !== "donation" && (
            <p className="text-[#0e2a1e]/40 text-xs mt-1" data-testid="gst-number-success">GST/HST No.: {BUSINESS_NUMBER}</p>
          )}

          {/* Scheduling */}
          {info?.kind !== "donation" && !scheduled && (
            <SchedulePicker sessionId={sessionId} kind={info?.kind} onScheduled={setScheduled} />
          )}
          {scheduled && (
            <div className="mt-8 bg-[#a4d61e]/15 rounded-2xl p-6 flex items-center gap-4 text-left">
              <CalendarCheck size={32} className="text-[#2f6b1e] shrink-0" />
              <div>
                <p className="font-semibold text-[#0e2a1e]">
                  {info?.kind === "booking" ? "Session requested" : "Pick-up confirmed"}
                </p>
                <p className="text-sm text-[#0e2a1e]/70">{scheduled.date} at {scheduled.time}</p>
                <p className="text-xs text-[#0e2a1e]/50 mt-1">A confirmation email has been sent. See you then!</p>
              </div>
            </div>
          )}

          <div className="mt-8">
            <Link to="/shop" className="bg-[#a4d61e] text-[#0e2a1e] font-semibold px-8 py-3.5 rounded-full hover:bg-[#b6e63a] transition-colors">
              Continue shopping
            </Link>
          </div>
        </div>
      )}
      {(state === "failed" || state === "timeout") && (
        <div className="flex flex-col items-center">
          <XCircle size={52} className="text-red-500" />
          <h1 className="font-serif-brand text-3xl font-semibold text-[#0e2a1e] mt-6">
            {state === "timeout" ? "Still processing" : "Payment not completed"}
          </h1>
          <p className="text-[#0e2a1e]/70 mt-3">
            {state === "timeout"
              ? "Your payment is taking longer than expected. If you were charged, you'll receive a confirmation shortly."
              : "Something went wrong with your payment. Please try again."}
          </p>
          <Link to="/cart" className="mt-8 border border-[#0e2a1e] text-[#0e2a1e] font-semibold px-8 py-3.5 rounded-full hover:bg-[#0e2a1e] hover:text-[#f4f2ea] transition-colors">
            Back to cart
          </Link>
        </div>
      )}
    </main>
  );
};

export default PaymentSuccess;
